import type { TFunction } from 'i18next';
import type { AnnualLeavePeriod, LeaveDuration } from './types';

/** 레거시 조회 폼(appr_view_forms_hr_*)의 표기 규칙 이식. 값 없음은 전부 '-' */

const EMPTY = '-';

/** "0900" → "09:00". 4자리가 아니면 원문 유지 */
export const toHHMM = (time: string | number | null | undefined): string => {
  if (time === null || time === undefined || time === '') return '';
  const t = String(time);
  return t.length === 4 ? `${t.slice(0, 2)}:${t.slice(2)}` : t;
};

/** "08:00 ~ 17:00" → "08:00~17:00" */
export const formatWorkingHoursRange = (
  value: string | null | undefined,
): string =>
  value
    ? value
        .split('~')
        .map((part) => part.trim())
        .join('~')
    : EMPTY;

/** { start: "0800", end: "1700" } → "08:00~17:00" */
export const formatWorkingHoursPair = (
  workingHours:
    | { start?: string | null; end?: string | null }
    | null
    | undefined,
): string =>
  workingHours?.start && workingHours?.end
    ? `${toHHMM(workingHours.start)}~${toHHMM(workingHours.end)}`
    : EMPTY;

/** "2026-06-01T…" / "2026-06-01 12:00:00" → "2026-06-01" */
export const formatDateOnly = (value: string | null | undefined): string => {
  if (!value) return EMPTY;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : value;
};

/**
 * 신청 기간 — 초 제거. 같은 날은 "2026-04-30 08:00~08:30", 다른 날은 종료일까지 표기
 */
export const formatPeriod = (
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
): string => {
  if (!startedAt || !endedAt) return EMPTY;
  const start = startedAt.replace(/:\d{2}$/, '');
  const end = endedAt.replace(/:\d{2}$/, '');
  const sameDay = start.slice(0, 10) === end.slice(0, 10);
  return sameDay ? `${start}~${end.slice(11)}` : `${start}~${end}`;
};

export const formatHours = (
  hours: number | null | undefined,
  t: TFunction,
): string => (hours == null ? EMPTY : t('{{hours}}시간', { hours }));

/** 구간 길이 → "1.5시간". 응답에 시간량 필드가 없어 계산 */
export const formatDurationHours = (
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
  t: TFunction,
): string => {
  if (!startedAt || !endedAt) return EMPTY;
  const start = new Date(startedAt.replace(' ', 'T'));
  const end = new Date(endedAt.replace(' ', 'T'));
  const hours = (end.getTime() - start.getTime()) / 3_600_000;
  if (Number.isNaN(hours) || hours <= 0) return EMPTY;
  return formatHours(
    Number.isInteger(hours) ? hours : Number(hours.toFixed(1)),
    t,
  );
};

/** "10 / 12시간" */
export const formatHoursOfLimit = (
  used: number | null | undefined,
  limit: number | null | undefined,
  t: TFunction,
): string =>
  used == null || limit == null ? EMPTY : `${used} / ${formatHours(limit, t)}`;

/** "1 / 4회" */
export const formatCountOfLimit = (
  count: number | null | undefined,
  limit: number | null | undefined,
  t: TFunction,
): string =>
  count == null || limit == null
    ? EMPTY
    : `${count} / ${t('{{count}}회', { count: limit })}`;

/** { day, hour, minute } → "1일 3시간". 0 인 단위 생략, 전부 0 이면 "0시간" */
export const formatLeaveDuration = (
  duration: LeaveDuration | null | undefined,
  t: TFunction,
): string => {
  if (!duration) return EMPTY;
  const parts: string[] = [];
  if (duration.day) parts.push(t('{{days}}일', { days: duration.day }));
  if (duration.hour) parts.push(t('{{hours}}시간', { hours: duration.hour }));
  if (duration.minute)
    parts.push(t('{{minutes}}분', { minutes: duration.minute }));
  return parts.length ? parts.join(' ') : t('{{hours}}시간', { hours: 0 });
};

/**
 * 휴가 기간 — 전체 구간을 "시작일 시작시각 ~ 종료일 종료시각" 한 줄로.
 * 전일·반차는 period 에 시각이 없어 근무시간으로 보완
 */
export const formatLeavePeriods = (
  periods: AnnualLeavePeriod[] | null | undefined,
  workingHours:
    | { start?: string | null; end?: string | null }
    | null
    | undefined,
): string => {
  if (!periods?.length) return EMPTY;
  const first = periods[0];
  const last = periods[periods.length - 1];
  const startTime = toHHMM(first.startTime || workingHours?.start);
  const endTime = toHHMM(last.endTime || workingHours?.end);
  const startLabel = `${first.startDate ?? ''}${
    startTime ? ` ${startTime}` : ''
  }`;
  const endLabel = `${last.endDate ?? ''}${endTime ? ` ${endTime}` : ''}`;
  return `${startLabel} ~ ${endLabel}`;
};
